import AddonSelector from './addons'
import ErrorBoundary from '@shared/error-boundary'
import Form from '@shared/form'
import FormImageInput from '@shared/form-image-input'
import FormInput from '@shared/form-input'
import FormSwitch from '@shared/form-switch'
import InventoryControlSelect from './inventory-control-select'
import NetsuiteAccountSelect from './netsuite-account-select'
import PromotionalCategoriesMultiSelect from '@shared/promotional-categories-multi-select'
import React, { ReactElement, ReactNode, useState } from 'react'
import TagsSelect from '@shared/tags-select'
import TaxCategorySelect from './tax-category-select'
import useAllActions from '@hooks/use-all-actions'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import {
  InventoryControlType,
  ProductOperatingState,
  ProductType,
  ProductTypeType,
} from '@types'
import { makeStyles } from 'tss-react/mui'
import { stripPrefix } from '@utils/string-utils'

const operatingStateLabels: Record<ProductOperatingState, string> = {
  [ProductOperatingState.hidden]: 'Hidden',
  [ProductOperatingState.visible]: 'Visible',
}

const ProductForm = ({
  canEdit,
  children,
  className,
  initialProductData,
  onSubmit,
  title,
}: Props): ReactElement => {
  const { classes, cx } = useStyles()
  const { setFlashMessage } = useAllActions()

  const isEditable = canEdit !== false
  const product: InitialProductData = initialProductData || {}

  const [imageUrl, setImageUrl] = useState(product.image_url)
  const [imageName, setImageName] = useState('')
  const [type, setType] = useState(product.product_type)
  // Edited through a text field, so this holds a string once the user types.
  const [price, setPrice] = useState<number | string | undefined>(product.price)
  const [name, setName] = useState(product.name || '')
  const [mobileMenuFormat, setMobileMenuFormat] = useState(
    product.mobile_menu_format || '',
  )
  const [squareSku, setSquareSku] = useState(product.square_sku || '')
  const [netsuiteAccountId, setNetsuiteAccountId] = useState<number | ''>(
    product.netsuite_account_id || '',
  )
  const [taxCategoryId, setTaxCategoryId] = useState<number | ''>(
    product.tax_category_id || '',
  )
  const [desc, setDesc] = useState(product.description || '')
  const [fgColor, setFgColor] = useState(
    stripPrefix('#', product.foreground_color || ''),
  )
  const [bgColor, setBgColor] = useState(
    stripPrefix('#', product.background_color || ''),
  )
  const [tColor, setTColor] = useState(
    stripPrefix('#', product.tertiary_color || ''),
  )
  const [calories, setCalories] = useState(product.calories || '')
  const [prepTime, setPrepTime] = useState(product.prep_time || '')
  const [isOnDefaultMenu, setIsOnDefaultMenu] = useState(
    product.on_default_menu || false,
  )
  const [inventoryControl, setInventoryControl] =
    useState<InventoryControlType>(product.inventory_control || 'unlimited')
  const [operatingState, setOperatingState] = useState<ProductOperatingState>(
    product.operating_state || ProductOperatingState.hidden,
  )
  const [isSeasonal, setIsSeasonal] = useState(product.is_seasonal || false)
  const [selectedAddons, setSelectedAddons] = useState(product.addons || [])
  const [tags, setTags] = useState(product.tags || [])
  const [promotionalCategories, setPromotionalCategories] = useState(
    product.promotional_categories || [],
  )

  const getImage = async () => {
    // No image URL => no Image
    if (imageUrl == null || imageUrl === product.image_url) return null

    // Get the base 64 image data
    return new Promise((resolve) => {
      fetch(imageUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const reader = new FileReader()
          reader.readAsDataURL(blob)
          reader.onloadend = () => {
            const base64Data = reader.result
            resolve(base64Data)
          }
        })
    })
  }

  const getProduct = async (): Promise<ProductFormData | null> => {
    const prepTimeNum = parseInt(prepTime, 10)
    if (prepTime !== '' && isNaN(prepTimeNum)) {
      setFlashMessage('Invalid prep time.', 'error')
      return null
    }

    const data: ProductFormData = {
      product_type: type,
      name,
      price,
      square_sku: squareSku,
      description: desc,
      calories,
      mobile_menu_format: mobileMenuFormat,
      prep_time: prepTimeNum,
      on_default_menu: isOnDefaultMenu,
      product_image_data: null,
      image_filename: null,
      foreground_color: null,
      background_color: null,
      tertiary_color: null,
      addons: selectedAddons,
      inventory_control: inventoryControl,
      operating_state: operatingState,
      is_seasonal: isSeasonal,
      tags,
      netsuite_account_id: netsuiteAccountId,
      tax_category_id: taxCategoryId,
      promotional_categories: promotionalCategories,
    }

    // Whether or not there's an image name also indicates whether or not a new image
    // was uploaded
    const image = await getImage()
    if (image != null && imageName != null) {
      data.product_image_data = image
      data.image_filename = imageName
    }

    if (!!fgColor && isValidHex(fgColor)) {
      data.foreground_color = '#' + fgColor
    } else if (!fgColor) {
      data.foreground_color = undefined
    }

    if (!!bgColor && isValidHex(bgColor)) {
      data.background_color = '#' + bgColor
    } else if (!bgColor) {
      data.background_color = undefined
    }

    if (!!tColor && isValidHex(tColor)) {
      data.tertiary_color = '#' + tColor
    } else if (!tColor) {
      data.tertiary_color = undefined
    }

    return data
  }

  const handleImageChange = (data) => {
    setImageName(data.name)
    setImageUrl(data.url)
  }

  const handleTypeChange = (event) => setType(event.target.value)

  const handleDefaultMenuToggle = () => setIsOnDefaultMenu(!isOnDefaultMenu)

  const handleSeasonalToggle = () => setIsSeasonal(!isSeasonal)

  const handleOperatingStateChange = (
    event: SelectChangeEvent<ProductOperatingState>,
  ) => setOperatingState(event.target.value as ProductOperatingState)

  const handleSubmit = async () => {
    const productData = await getProduct()

    if (productData != null) onSubmit(productData)
  }

  return (
    <Form className={className} title={title}>
      <Grid container spacing={4}>
        <Grid item lg={4} xs={6}>
          <FormImageInput
            imageUrl={imageUrl}
            isEditing={isEditable}
            onChange={handleImageChange}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormControl className={classes.type} component='fieldset'>
            <FormLabel component='legend'>Product Type</FormLabel>
            <RadioGroup
              aria-label='type'
              name='type'
              onChange={handleTypeChange}
              value={type}
            >
              <FormControlLabel
                control={<Radio />}
                disabled={!isEditable}
                label='Beverage'
                value='beverage'
              />
              <FormControlLabel
                control={<Radio />}
                disabled={!isEditable}
                label='Food'
                value='food'
              />
              <FormControlLabel
                control={<Radio />}
                disabled={!isEditable}
                label='Bag'
                value='bag'
              />
              <FormControlLabel
                control={<Radio />}
                disabled={!isEditable}
                label='Bottle'
                value='bottle'
              />
              <FormControlLabel
                control={<Radio />}
                disabled={!isEditable}
                label='Merchandise'
                value='merchandise'
              />
              <FormControlLabel
                control={<Radio />}
                disabled={!isEditable}
                label='To-Go'
                value='togo'
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormControl className={classes.control} fullWidth>
            <InputLabel id='operating-state-label'>Operating State</InputLabel>
            <Select
              disabled={!isEditable}
              label='Operating State'
              labelId='operating-state-label'
              onChange={handleOperatingStateChange}
              value={operatingState}
            >
              {Object.values(ProductOperatingState).map((option) => (
                <MenuItem key={option} value={option}>
                  {operatingStateLabels[option]}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Hidden products are not shown to customers.
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setName}
            title='Name'
            value={name}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setPrice}
            title='Base Price (in cents)'
            value={price}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <ErrorBoundary>
            <FormControl
              className={cx(classes.tags, classes.control)}
              fullWidth
            >
              <TagsSelect
                disabled={!isEditable}
                onChange={setTags}
                selectedTags={tags}
              />
            </FormControl>
          </ErrorBoundary>
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{
              disabled: product.square_sku != null,
            }}
            onChangeText={setSquareSku}
            title='SKU'
            value={squareSku}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <NetsuiteAccountSelect
            disabled={!isEditable || product.netsuite_account_id != null}
            formControlProps={{ fullWidth: true }}
            onChange={setNetsuiteAccountId}
            value={netsuiteAccountId}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <TaxCategorySelect
            disabled={!isEditable}
            formControlProps={{ fullWidth: true }}
            onChange={setTaxCategoryId}
            value={taxCategoryId}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setDesc}
            title='Description'
            value={desc}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{
              startAdornment: (
                <InputAdornment position='start'>#</InputAdornment>
              ),
              endAdornment: <ColorDisplay color={fgColor} />,
            }}
            onChangeText={setFgColor}
            title={'Foreground Color'}
            value={fgColor}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{
              startAdornment: (
                <InputAdornment position='start'>#</InputAdornment>
              ),
              endAdornment: <ColorDisplay color={bgColor} />,
            }}
            onChangeText={setBgColor}
            title={'Background Color'}
            value={bgColor}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{
              startAdornment: (
                <InputAdornment position='start'>#</InputAdornment>
              ),
              endAdornment: <ColorDisplay color={tColor} />,
            }}
            onChangeText={setTColor}
            title={'Tertiary Color'}
            value={tColor}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{
              placeholder: 'e.g. 400-700',
            }}
            onChangeText={setCalories}
            title={'Calories (Optional)'}
            value={calories}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setPrepTime}
            title={'Prep Time (Optional)'}
            value={prepTime}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormSwitch
            checked={isOnDefaultMenu}
            disabled={!isEditable}
            helperText='Setting this to true will make this product appear in the default menu (when no store is selected)'
            marginTop={16}
            onChange={handleDefaultMenuToggle}
            title='Show on the Default Menu'
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <InventoryControlSelect
            disabled={!isEditable}
            formControlProps={{ fullWidth: true }}
            onChange={setInventoryControl}
            value={inventoryControl}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormSwitch
            checked={isSeasonal}
            disabled={!isEditable}
            helperText='Setting this to true will simply flag the product as a seasonal or limited time promotion item.'
            marginBottom={16}
            marginTop={16}
            onChange={handleSeasonalToggle}
            title='Seasonal/Promotional Product'
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <ErrorBoundary>
            <FormControl
              className={cx(classes.tags, classes.control)}
              fullWidth
            >
              <PromotionalCategoriesMultiSelect
                disabled={!isEditable}
                onChange={setPromotionalCategories}
                value={promotionalCategories}
              />
              <FormHelperText>
                The helps categorize which groups the product falls into when
                managing promotion exclusions.{' '}
              </FormHelperText>
            </FormControl>
          </ErrorBoundary>
        </Grid>
        <Grid item lg={4} xs={6}>
          <Typography variant='subtitle1'>Addons</Typography>
          <AddonSelector
            disabled={!isEditable}
            selectedAddons={product.addons}
            updateAddons={setSelectedAddons}
          />
          {children}
        </Grid>
        <Grid item xs={12}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setMobileMenuFormat}
            title='Mobile Menu string'
            value={mobileMenuFormat}
          />
        </Grid>
      </Grid>
      {isEditable && (
        <Button className={classes.submitButton} onClick={handleSubmit}>
          Submit
        </Button>
      )}
    </Form>
  )
}

export default ProductForm

const ColorDisplay = ({ color }: ColorDisplayProps): ReactElement => {
  const { classes } = useStyles()
  const isColorValid = isValidHex(color)
  const backgroundColor = isColorValid ? `#${color}` : '#fff'

  return (
    <Box
      boxShadow={1}
      className={classes.colorDisplay}
      style={{ backgroundColor }}
    />
  )
}

/**
 * The subset of a product the form knows how to edit. Not a `ProductType` —
 * it carries the uploaded image payload and omits server-owned fields.
 */
export type ProductFormData = {
  product_type?: ProductTypeType
  name: string
  price?: number | string
  square_sku: string
  description: string
  calories?: any
  mobile_menu_format: string
  prep_time: number
  on_default_menu: boolean
  product_image_data?: any
  image_filename?: string | null
  foreground_color?: string | null
  background_color?: string | null
  tertiary_color?: string | null
  addons?: any
  inventory_control: InventoryControlType
  operating_state?: ProductOperatingState
  is_seasonal: boolean
  tags?: any
  netsuite_account_id?: number | string
  tax_category_id?: number | string
  promotional_categories?: any
}

/**
 * `promotional_categories` isn't on `ProductType` yet, and every field is
 * optional so the form can also render for a brand new product.
 */
type InitialProductData = Partial<ProductType> & {
  promotional_categories?: any
}

type Props = {
  canEdit?: boolean
  children?: ReactNode
  className?: string
  initialProductData?: InitialProductData
  onSubmit: (data: ProductFormData) => void | Promise<void>
  title?: string
}

type ColorDisplayProps = {
  color?: string
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
  },
  preview: {
    marginLeft: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  colorDisplay: {
    height: 16,
    width: 16,
    marginBottom: theme.spacing(1),
  },
  submitButton: {
    marginTop: 24,
    backgroundColor: theme.palette.secondary.main,
    color: 'white',
  },
  type: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  control: {
    marginBottom: theme.spacing(1),
  },
  imageContainer: {
    width: '100%',
  },
  image: {
    height: 180,
  },
  tags: {
    marginLeft: theme.spacing(1),
  },
}))

function isValidHex(hex?: string) {
  return (
    typeof hex === 'string' && hex.length === 6 && !isNaN(Number('0x' + hex))
  )
}
